import React from "react";
import { Link } from "react-router-dom";

import {
  ETAPAS,
  ETAPAS_COM_MOTIVO,
  formatarMinutos,
  textoNotasEnvios,
  textoPedidos,
  tituloCartao,
  tomEnvelhecimento,
} from "../../../Trackfy/DespachoIndicadores/regras";
import "./quadroPedidos.css";

// Quadro (kanban) do despacho (PO1-3500, épico PO1-3493): uma coluna por etapa,
// cartão = pedido (ou NF sem pedido), do mais parado para o menos. Só leitura,
// sem arrastar entre colunas.
//
// Componente PURO (só props): a busca fica com quem usa — o bloco da Torre e o
// widget do Meu Painel (QuadroDespacho, via useQuadroDespacho). Carregando e
// erro ficam na casca (CartaoGrafico). CSS próprio (quadroPedidos.css), com
// classes despacho-quadro__* porque assets/css/cards.css é global e já define
// .kanban-column.
//
// props:
//   colunas      : as 7 colunas de normalizarQuadro (services/despachoIndicadoresApi).
//   linkVerTodos : (etapa) => href do "Ver todos" (lista filtrada na etapa).
//   onAbrirPedido, onAbrirLote, onAbrirNota : abrem o detalhe em MODAL, sem
//                  sair da tela (decisão do Rodrigo, 17/10). Sem a função, o
//                  atalho não aparece (quem usa decide pela permissão).
//   legenda      : mostra a legenda das cores de envelhecimento (padrão: sim).

const ROTULO_ENVELHECIMENTO = {
  verde: "Parado há até 4 h",
  ambar: "Parado entre 4 h e 24 h",
  vermelho: "Parado há mais de 24 h",
  neutro: "Sem data para medir o tempo parado",
};

export function LegendaEnvelhecimento() {
  return (
    <div className="despacho-quadro__legenda" aria-label="Tempo parado">
      <span className="despacho-quadro__legenda-item despacho-quadro__legenda-item--verde">Até 4 h</span>
      <span className="despacho-quadro__legenda-item despacho-quadro__legenda-item--ambar">4 h a 24 h</span>
      <span className="despacho-quadro__legenda-item despacho-quadro__legenda-item--vermelho">Mais de 24 h</span>
    </div>
  );
}

function Cartao({ item, etapa, onAbrirNota, onAbrirPedido, onAbrirLote }) {
  const tom = tomEnvelhecimento(item.minutosParado);
  const extras = Number(item.qtdTransportadoras) > 1 ? Number(item.qtdTransportadoras) - 1 : 0;
  const local = [item.cidade, item.uf].filter(Boolean).join("/");
  const motivo = ETAPAS_COM_MOTIVO.includes(etapa) ? item.motivo : null;
  return (
    <article className={`despacho-quadro__cartao despacho-quadro__cartao--${tom}`} data-envelhecimento={tom}>
      <header className="despacho-quadro__cartao-topo">
        <strong className="despacho-quadro__cartao-titulo" title={tituloCartao(item)}>
          {tituloCartao(item)}
        </strong>
        <span className="despacho-quadro__tempo" title={ROTULO_ENVELHECIMENTO[tom]}>
          {formatarMinutos(item.minutosParado)}
        </span>
      </header>
      <div className="despacho-quadro__linha" title={item.destinatario || ""}>
        {item.destinatario || "-"}
      </div>
      {local ? <div className="despacho-quadro__linha despacho-quadro__linha--suave">{local}</div> : null}
      <div className="despacho-quadro__linha despacho-quadro__linha--transportadora">
        <span className="despacho-quadro__transportadora" title={item.transportadora || ""}>
          {item.transportadora || "-"}
        </span>
        {extras ? (
          <span className="despacho-quadro__mais" title={`Mais ${extras} transportadora(s) neste pedido`}>
            +{extras}
          </span>
        ) : null}
      </div>
      <div className="despacho-quadro__linha despacho-quadro__linha--suave">{textoNotasEnvios(item.qtdNotas, item.qtdEnvios)}</div>
      {item.codigoLote ? (
        <div className="despacho-quadro__linha despacho-quadro__linha--suave" title={item.codigoLote}>
          Lote {item.codigoLote}
        </div>
      ) : null}
      {motivo ? (
        <div className="despacho-quadro__motivo" title={motivo}>
          {motivo}
        </div>
      ) : null}
      <div className="despacho-quadro__atalhos">
        {item.pedido && onAbrirPedido ? (
          <button type="button" className="despacho-quadro__atalho despacho-quadro__atalho--botao" title="Abrir o pedido" onClick={() => onAbrirPedido(item.pedido)}>
            Pedido
          </button>
        ) : null}
        {onAbrirNota && Number(item.qtdNotas) === 1 && item.idNotaFiscal ? (
          // Uma NF no cartão: abre o detalhe da NF-e (o mesmo modal da Consulta de NF-e e da Auditoria de CT-e).
          <button type="button" className="despacho-quadro__atalho despacho-quadro__atalho--botao" title="Abrir o detalhe da NF-e" onClick={() => onAbrirNota(item.idNotaFiscal)}>
            NF-e
          </button>
        ) : null}
        {item.idLote && onAbrirLote ? (
          <button type="button" className="despacho-quadro__atalho despacho-quadro__atalho--botao" title={item.codigoLote ? `Abrir o lote ${item.codigoLote}` : "Abrir o lote"} onClick={() => onAbrirLote(item.idLote)}>
            Lote
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default function QuadroPedidos({ colunas, linkVerTodos, onAbrirNota, onAbrirPedido, onAbrirLote, legenda = true }) {
  const porEtapa = (colunas || []).reduce((acc, c) => {
    acc[c.etapa] = c;
    return acc;
  }, {});
  return (
    <div className="despacho-quadro-raiz">
      {legenda ? <LegendaEnvelhecimento /> : null}
      <div className="despacho-quadro" role="list" aria-label="Pedidos por etapa">
        {ETAPAS.map((def) => {
          const col = porEtapa[def.valor] || { total: 0, itens: [] };
          const itens = col.itens || [];
          return (
            <section key={def.valor} className="despacho-quadro__coluna" data-etapa={def.valor} role="listitem" aria-label={def.rotulo}>
              <header className="despacho-quadro__cabecalho" title={def.ajuda}>
                <span className="despacho-quadro__nome">{def.rotulo}</span>
                <span className="despacho-quadro__contador">{textoPedidos(col.total)}</span>
              </header>
              <div className="despacho-quadro__corpo">
                {itens.length === 0 ? <div className="despacho-quadro__vazio">Nenhum pedido</div> : itens.map((item, i) => <Cartao key={item.chave || `${def.valor}-${i}`} item={item} etapa={def.valor} onAbrirNota={onAbrirNota} onAbrirPedido={onAbrirPedido} onAbrirLote={onAbrirLote} />)}
              </div>
              {col.total > 0 ? (
                <div className="despacho-quadro__rodape">
                  <span>
                    Mostrando {itens.length} de {col.total}
                  </span>
                  {linkVerTodos ? (
                    <Link to={linkVerTodos(def.valor)} className="despacho-quadro__ver-todos" title={`Ver todos os envios em "${def.rotulo}"`}>
                      Ver todos
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
